import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * ✅ 트랙의 "실제 한 칸 이동 거리(stepPx)"를 DOM에서 측정
 * - offsetLeft(정수) 대신 getBoundingClientRect().left(실수) 기반으로 측정 → 라운딩 오차로 인한 1~2px 깨짐 방지
 * - 1개만 있을 때는 width 사용
 * - ResizeObserver로 화면/반응형 변경 시 자동 갱신
 */
function useMeasuredStep(trackRef) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const measure = () => {
      const kids = track.children;
      if (!kids || kids.length === 0) return;

      if (kids.length >= 2) {
        const a = kids[0].getBoundingClientRect();
        const b = kids[1].getBoundingClientRect();
        const s = b.left - a.left; // ✅ float
        setStep(s);
      } else {
        setStep(kids[0].getBoundingClientRect().width);
      }
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(track);
    if (track.parentElement) ro.observe(track.parentElement);

    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [trackRef]);

  return step;
}

/**
 * ✅ Seamless infinite slider (px 기준)
 * - cloneCount 만큼 앞/뒤에 복제
 * - transitionEnd에서 clone영역 진입 시 "보이지 않게" 점프
 * - autoplay interval 제공
 *
 * ✅ 핵심 Fix:
 * - stepPx가 0 → 값으로 바뀌는 순간(측정 완료) transform이 크게 변함
 * - 이때 transition이 켜져 있으면 "처음 들어가자마자 휙"처럼 보임
 * - 그래서 stepPx 변동 시에는 무조건 noAnim으로 점프 후에만 autoplay 시작
 */
function useInfinitePxSlider({
  len,
  stepPx,
  intervalMs,
  enabled,
  cloneCount,
  startDelayMs = 0,
}) {
  const [idx, setIdx] = useState(cloneCount);
  const [noAnim, setNoAnim] = useState(true); // ✅ 처음엔 애니메이션 끔(중요)
  const timerRef = useRef(null);
  const delayRef = useRef(null);

  // ✅ step이 "처음으로 유효해진 순간"을 감지
  const prevStepRef = useRef(0);

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;

    if (delayRef.current) clearTimeout(delayRef.current);
    delayRef.current = null;
  };

  const start = () => {
    if (!enabled || !len || !stepPx || !intervalMs) return;
    stop();

    const run = () => {
      timerRef.current = setInterval(() => setIdx((p) => p + 1), intervalMs);
    };

    if (startDelayMs > 0) {
      delayRef.current = setTimeout(run, startDelayMs);
    } else {
      run();
    }
  };

  const next = () => setIdx((p) => p + 1);
  const prev = () => setIdx((p) => p - 1);

  const jumpSeamless = (newIdx) => {
    stop();
    setNoAnim(true);
    setIdx(newIdx);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setNoAnim(false);
        // 점프 후에는 delay 없이 바로 재개
        if (enabled && len && stepPx && intervalMs) {
          timerRef.current = setInterval(() => setIdx((p) => p + 1), intervalMs);
        }
      });
    });
  };

  const onTransitionEnd = () => {
    if (!enabled || !len) return;

    if (idx < cloneCount) jumpSeamless(idx + len);
    else if (idx >= cloneCount + len) jumpSeamless(idx - len);
  };

  useEffect(() => {
    // 유효하지 않으면 정지 + 기본 위치
    if (!enabled || !len) {
      stop();
      setNoAnim(true);
      setIdx(cloneCount);
      return;
    }

    // ✅ stepPx가 아직 0이면(측정 전) 일단 애니메이션 끄고 기다림
    if (!stepPx || stepPx <= 0) {
      stop();
      setNoAnim(true);
      setIdx(cloneCount); // 첫 슬라이드 위치로 "준비"
      prevStepRef.current = stepPx || 0;
      return;
    }

    // ✅ 여기부터 stepPx 유효
    const prevStep = prevStepRef.current;
    prevStepRef.current = stepPx;

    // ✅ step이 변하는 순간(특히 0→값 / 반응형 변경)은 무조건 noAnim 점프
    stop();
    setNoAnim(true);
    setIdx(cloneCount);

    // ✅ 2프레임 후 애니메이션 ON + autoplay 시작(첫 시작은 delay 적용)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setNoAnim(false);
        // step이 변한 직후라도 autoplay는 정상 시작
        start();
      });
    });

    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, len, stepPx, intervalMs, cloneCount, startDelayMs]);

  const style = {
    transform: `translateX(-${idx * (stepPx || 0)}px)`,
    transition: noAnim ? "none" : "transform 0.35s ease",
  };

  return { idx, next, prev, style, onTransitionEnd };
}

const HomePage = ({ data }) => {
  // ✅ 훅은 무조건 최상단에서 호출 (조건부 호출 금지)
  const heroTrackRef = useRef(null);
  const servicesTrackRef = useRef(null);
  const reviewsTrackRef = useRef(null);
  const galleryTrackRef = useRef(null);

  const heroImages = useMemo(() => data?.hero?.images || [], [data]);
  const serviceCards = useMemo(() => data?.services?.cards || [], [data]);
  const reviewCards = useMemo(() => data?.reviews || [], [data]);
  const galleryImages = useMemo(() => data?.gallery || [], [data]);

  // ✅ cloneCount: 화면에 보이는 개수만큼이 가장 안정적
  const heroClone = 1;
  const listClone = 4;

  // ✅ stepPx를 DOM에서 측정 (반응형에 자동 대응)
  const heroStep = useMeasuredStep(heroTrackRef);
  const servicesStep = useMeasuredStep(servicesTrackRef);
  const reviewsStep = useMeasuredStep(reviewsTrackRef);
  const galleryStep = useMeasuredStep(galleryTrackRef);

  // ✅ 무한 루프용 렌더 배열(앞/뒤 clone)
  const heroRender = useMemo(() => {
    if (!heroImages.length) return [];
    const head = heroImages.slice(-heroClone);
    const tail = heroImages.slice(0, heroClone);
    return [...head, ...heroImages, ...tail];
  }, [heroImages]);

  const servicesRender = useMemo(() => {
    if (!serviceCards.length) return [];
    const c = Math.min(listClone, serviceCards.length);
    const head = serviceCards.slice(-c);
    const tail = serviceCards.slice(0, c);
    return [...head, ...serviceCards, ...tail];
  }, [serviceCards]);

  const reviewsRender = useMemo(() => {
    if (!reviewCards.length) return [];
    const c = Math.min(listClone, reviewCards.length);
    const head = reviewCards.slice(-c);
    const tail = reviewCards.slice(0, c);
    return [...head, ...reviewCards, ...tail];
  }, [reviewCards]);

  const galleryRender = useMemo(() => {
    if (!galleryImages.length) return [];
    const c = Math.min(4, galleryImages.length);
    const head = galleryImages.slice(-c);
    const tail = galleryImages.slice(0, c);
    return [...head, ...galleryImages, ...tail];
  }, [galleryImages]);

  // ✅ 슬라이더 컨트롤
  const heroSlider = useInfinitePxSlider({
    len: heroImages.length,
    stepPx: heroStep,
    intervalMs: 4500,
    enabled: heroImages.length > 1,
    cloneCount: heroClone,
    startDelayMs: 4500, // ✅ 첫 진입 시 첫 장 유지
  });

  const servicesSlider = useInfinitePxSlider({
    len: serviceCards.length,
    stepPx: servicesStep,
    intervalMs: 3500,
    enabled: serviceCards.length > 1,
    cloneCount: Math.min(listClone, serviceCards.length),
  });

  const reviewsSlider = useInfinitePxSlider({
    len: reviewCards.length,
    stepPx: reviewsStep,
    intervalMs: 4000,
    enabled: reviewCards.length > 1,
    cloneCount: Math.min(listClone, reviewCards.length),
  });

  const gallerySlider = useInfinitePxSlider({
    len: galleryImages.length,
    stepPx: galleryStep,
    intervalMs: 2500,
    enabled: galleryImages.length > 1,
    cloneCount: Math.min(4, galleryImages.length),
  });

  // ✅ Appear-on-scroll + stat counters (너 기존 로직 유지)
  useEffect(() => {
    if (!data) return;

    const animatedSel =
      ".stat-item, .service-card, .partner-logo, .instructor-card, .stat-number, .review-card, .gallery-item";
    const items = document.querySelectorAll(animatedSel);
    items.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      el.style.transition = "all 0.6s ease";
    });

    const animateCounter = (numberEl) => {
      if (!numberEl) return;
      const unitEl = numberEl.querySelector(".stat-unit");
      const raw = numberEl.textContent.replace(/[^0-9]/g, "");
      const target = parseInt(raw, 10) || 0;

      let host = numberEl.querySelector(".stat-value-host");
      if (!host) {
        host = document.createElement("span");
        host.className = "stat-value-host";
        numberEl.innerHTML = "";
        numberEl.appendChild(host);
        if (unitEl) numberEl.appendChild(unitEl);
      }

      let current = 0;
      const steps = 100;
      const inc = target / steps;
      const timer = setInterval(() => {
        current += inc;
        if (current >= target) {
          host.textContent = target.toLocaleString();
          clearInterval(timer);
        } else {
          host.textContent = Math.floor(current).toLocaleString();
        }
      }, 20);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            if (el.classList.contains("stat-number")) {
              animateCounter(el);
            }
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [data]);

  if (!data) return null;

  // ===== URL helper (services 카드 클릭용) =====
  const getUrl = (c) => (c?.url ?? c?.href ?? c?.link ?? "").trim();
  const isClickable = (c) => {
    const u = getUrl(c);
    return u !== "" && u !== "#";
  };
  const openInNewTab = (url) => {
    if (!url || url === "#") return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* ================= Hero ================= */}
      <section className="hero hero-slider" id="home" aria-label="메인 히어로">
        <div
          className="hero-track"
          ref={heroTrackRef}
          style={heroSlider.style}
          onTransitionEnd={heroSlider.onTransitionEnd}
        >
          {heroRender.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className="hero-slide"
              style={{ backgroundImage: `url(${url})` }}
            />
          ))}
        </div>

        <div className="hero-content">
          <h1
            dangerouslySetInnerHTML={{
              __html: (data.hero?.title || "").replaceAll("\n", "<br/>"),
            }}
          />
          <Link
            className="view-more-btn"
            to={data.hero?.ctaTarget || "/courses"}
            aria-label="강습과정 보기"
          >
            {data.hero?.ctaText || "VIEW MORE"}
          </Link>
        </div>

        <div className="slider-nav" aria-label="히어로 이미지 내비게이션">
          <button
            className="button-prev"
            onClick={heroSlider.prev}
            aria-label="이전 이미지"
            type="button"
          >
            ←
          </button>
          <button
            className="button-next"
            onClick={heroSlider.next}
            aria-label="다음 이미지"
            type="button"
          >
            →
          </button>
        </div>
      </section>

      {/* ================= Stats ================= */}
      <section className="stats" id="education">
        <div className="container">
          <div className="stats-header">
            <div>
              <h2 className="stats-title">{data.stats?.title}</h2>
              <p
                className="stats-description"
                dangerouslySetInnerHTML={{
                  __html: (data.stats?.desc || "").replaceAll("\n", "<br/>"),
                }}
              />
            </div>
            <div>
              <p className="stats-background">{data.stats?.bgWord}</p>
            </div>
          </div>

          <div className="stats-grid">
            {(data.stats?.items || []).map((s, idx) => (
              <div className="stat-item" key={idx}>
                <div className="stat-number">
                  {(s?.value ?? 0).toLocaleString()}
                  <span className="stat-unit">{s?.unit}</span>
                </div>
                <div className="stat-label">{s?.label}</div>
              </div>
            ))}
          </div>

          <div className="stats-content">
            <div
              className="stats-image"
              style={{
                backgroundImage: `url(${
                  data.stats?.image?.url || data.stats?.image || ""
                })`,
              }}
            />
            <div className="stats-text">
              {(data.stats?.textBlocks || []).map((b, i) => (
                <div key={i}>
                  <h3
                    dangerouslySetInnerHTML={{
                      __html: (b?.h3 || "").replaceAll("\n", "<br/>"),
                    }}
                  />
                  {(b?.p || []).map((pp, j) => (
                    <p key={j}>{pp}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= Services ================= */}
      <section className="services" id="community">
        <div className="container">
          <div className="services-header">
            <h2 className="services-title">{data.services?.title}</h2>
            <p className="services-description">{data.services?.desc}</p>
          </div>

          <div className="services-slider">
            <div
              className="services-wrapper"
              ref={servicesTrackRef}
              style={servicesSlider.style}
              onTransitionEnd={servicesSlider.onTransitionEnd}
            >
              {servicesRender.map((c, i) => {
                const clickable = isClickable(c);
                const url = getUrl(c);
                return (
                  <div
                    className="service-card"
                    key={`${c?.title || "service"}-${i}`}
                    style={
                      c?.bg
                        ? { backgroundImage: `url(${c.bg})` }
                        : { backgroundColor: "#333" }
                    }
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={() => clickable && openInNewTab(url)}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openInNewTab(url);
                      }
                    }}
                    title={clickable ? "새 창에서 열기" : undefined}
                  >
                    <h3>{c?.title}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ================= Partners ================= */}
      <section className="partners">
        <div className="container">
          <div className="partners-header">
            <h2 className="partners-title">다이빙이지 파트너스</h2>
            <p className="partners-description">
              안전하고 체계적인 교육을 위해 믿을 수 있는
              <br />
              파트너 기관들과 함께하고 있습니다.
            </p>
          </div>

          <div className="partners-grid">
            {(data.partners || []).map((p, i) => (
              <div className="partner-logo" key={i}>
                <img src={p.image} alt={p.name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Reviews ================= */}
      <section className="reviews">
        <div className="container">
          <div className="reviews-header">
            <h2 className="reviews-title">다이빙이지와 함께한 리뷰</h2>
            <p className="reviews-description">
              다이빙이지와 함께한 생생한 리뷰를 만나보세요.
            </p>
          </div>

          <div className="reviews-viewport">
            <div
              className="reviews-track"
              ref={reviewsTrackRef}
              style={reviewsSlider.style}
              onTransitionEnd={reviewsSlider.onTransitionEnd}
            >
              {reviewsRender.map((r, i) => (
                <div className="review-card" key={`${r?.name || "review"}-${i}`}>
                  <div className="review-image">
                    <img src={r.avatar} alt={`${r.name} 후기`} />
                  </div>
                  <p className="review-text">"{r.text}"</p>
                  <p className="review-name">강습생 {r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= Gallery ================= */}
      <section className="gallery">
        <div className="gallery-viewport">
          <div
            className="gallery-track"
            ref={galleryTrackRef}
            style={gallerySlider.style}
            onTransitionEnd={gallerySlider.onTransitionEnd}
          >
            {galleryRender.map((url, i) => (
              <div className="gallery-item" key={`${url}-${i}`}>
                <img src={url} alt={`gallery-${i}`} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
